import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
} from "@mui/material";
import {
  CalendarToday,
  Pets,
  NavigateBefore,
  NavigateNext,
  FavoriteOutlined,
} from "@mui/icons-material";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface ReproductiveRecord {
  _key: string;
  _id: string;
  _rev: string;
  farm_id: string;
  cattle_tag: string;
  handling_type_code: string;
  title: string;
  handling_date: string;
  performed_by?: string;
  notes?: string;
  cost?: number | null;
  operation_lot_id?: string;
  handling_details: {
    touro_id?: string;
    [key: string]: any;
  };
  created_at: string;
  updated_at: string;
}

export interface ReproductiveHistoryData {
  data: ReproductiveRecord[];
  resumo: string;
  url: string;
}

interface ReproductiveHistoryViewProps {
  reproductiveHistoryData: ReproductiveHistoryData;
}

const ITEMS_PER_PAGE = 10;

export const ReproductiveHistoryView: React.FC<ReproductiveHistoryViewProps> = ({
  reproductiveHistoryData,
}) => {
  const { t } = useTranslation(['farm', 'common']);
  const [currentPage, setCurrentPage] = useState(1);

  const reproductiveHistory = reproductiveHistoryData.data || [];
  const totalPages = Math.ceil(reproductiveHistory.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentItems = reproductiveHistory.slice(startIndex, endIndex);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const getHandlingTypeLabel = (handlingType: string) => {
    const typeMap: { [key: string]: string } = {
      'inseminacao_artificial': 'Inseminação Artificial',
      'monta_natural': 'Monta Natural',
      'diagnostico_gestacao': 'Diagnóstico de Gestação',
      'parto': 'Parto',
      'secagem': 'Secagem',
      'cio': 'Cio',
    };
    return typeMap[handlingType] || handlingType;
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        minHeight: 0,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#a8a8a8",
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        {/* Header com informações */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <Chip
                label={`Registros: ${reproductiveHistory.length}`}
                size="small"
                variant="outlined"
              />
              {reproductiveHistory.length > 0 && (
                <Chip
                  label={`Lote: ${reproductiveHistory[0].operation_lot_id || 'N/A'}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            {reproductiveHistoryData.resumo && (
              <Typography
                variant="body2"
                sx={{
                  color: DESIGN_TOKENS.colors.text.secondary,
                  fontStyle: "italic",
                }}
              >
                {reproductiveHistoryData.resumo}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Estatísticas Reprodutivas
            </Typography>
            <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FavoriteOutlined sx={{ color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total de Registros
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {reproductiveHistory.length}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img 
                  src="/gestores/reproducao.svg" 
                  alt="Animais Envolvidos" 
                  style={{ width: 24, height: 24 }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Animais Envolvidos
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {new Set(reproductiveHistory.map(r => r.cattle_tag)).size}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarToday sx={{ color: "text.secondary" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Último Registro
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {reproductiveHistory.length > 0 
                      ? formatDate(reproductiveHistory[0].handling_date)
                      : 'N/A'
                    }
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Tabela de histórico */}
        {reproductiveHistory.length > 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <img 
                  src="/gestores/reproducao.svg" 
                  alt="Reprodução" 
                  style={{ width: 24, height: 24 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Registros Reprodutivos ({reproductiveHistory.length})
                </Typography>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CalendarToday sx={{ fontSize: 18, color: "text.secondary" }} />
                          Data
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <img 
                            src="/gestores/reproducao.svg" 
                            alt="Animal" 
                            style={{ width: 18, height: 18 }}
                          />
                          Animal
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <img 
                            src="/gestores/reproducao.svg" 
                            alt="Tipo" 
                            style={{ width: 18, height: 18 }}
                          />
                          Tipo de Manejo
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Detalhes
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentItems.map((record, index) => (
                      <TableRow key={record._id || index} hover>
                        <TableCell>
                          {formatDate(record.handling_date)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "grey.300",
                                color: "text.primary",
                                fontSize: "0.875rem",
                              }}
                            >
                              {record.cattle_tag}
                            </Avatar>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getHandlingTypeLabel(record.handling_type_code)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            {record.handling_details?.touro_id && (
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Touro:</strong> {record.handling_details.touro_id}
                              </Typography>
                            )}
                            {record.operation_lot_id && (
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Lote:</strong> {record.operation_lot_id}
                              </Typography>
                            )}
                            {record.notes && (
                              <Typography variant="body2" color="text.secondary">
                                {record.notes}
                              </Typography>
                            )}
                            {record.performed_by && (
                              <Typography variant="caption" color="text.secondary">
                                Por: {record.performed_by}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginação */}
              {totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 3,
                    gap: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<NavigateBefore />}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setCurrentPage(page)}
                        sx={{ minWidth: 40 }}
                      >
                        {page}
                      </Button>
                    )
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<NavigateNext />}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                }}
              >
                <img 
                  src="/gestores/reproducao.svg" 
                  alt="Nenhum registro reprodutivo" 
                  style={{ 
                    width: 64, 
                    height: 64, 
                    opacity: 0.5,
                    marginBottom: 16 
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhum registro reprodutivo encontrado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Não há registros reprodutivos para exibir no momento.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default ReproductiveHistoryView;